import { isFunction, pickBy, reduce } from 'lodash';
import { FirestoreChangeTypes } from '@sda/enums';
import type { EntityAdapter } from '@client/store/entity.adapter';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { StateItemList, UpsertItem, Item } from './interfaces';
import type { ExtendedParsedToken, ExtendedUser, ResultFirebaseToken } from '@sda/interfaces/reducer/session.reducer';
import type FirebaseFirestore from 'firebase/firestore';

/**
 * Private upsert helper method for reconciliation of state
 * @param  {StateItemList} stateItems - the state
 * @param  {Item} upsertItem - the item to be upserted
 * @param  {string} identityAttr - the identity to be matched
 * @return {StateItemList}
 */
const upsert = <T>(stateItems: StateItemList<T>, upsertItem: T, identityAttr: string): StateItemList<T> => {
  if (stateItems && stateItems[upsertItem[identityAttr]]) {
    return {
      [upsertItem[identityAttr]]: {
        ...stateItems[upsertItem[identityAttr]],
        ...upsertItem,
      },
    };
  }

  if (upsertItem[identityAttr]) {
    return {
      [upsertItem[identityAttr]]: upsertItem,
    };
  }

  return {};
};

/**
 * Upserts store item matching identity attribute
 * @param  {StateItemList} stateItems - the state
 * @param  {UpsertItem} upsertItem - the item(s) to be upserted
 * @param  {string} identityAttr - the identity attribute to match
 * @param  {boolean} preserveRemovedObjects
 * @return {StateItemList}
 */
export const reducerUpsertHelper = <T>(
  stateItems: StateItemList<T>,
  upsertItem: UpsertItem<T>,
  identityAttr: string,
  preserveRemovedObjects = false,
): StateItemList<T> => {
  if (upsertItem) {
    if (upsertItem.payload || upsertItem.modifier) {
      const { payload = {}, modifier = {} } = upsertItem;

      if (Object.keys(payload).length) {
        const items = Object.values(payload);

        return reduce(
          items,
          (results, item: T & Item) => {
            if (
              !preserveRemovedObjects
              && item
              && item.FIRESTORE_CHANGE_TYPE
              && item.FIRESTORE_CHANGE_TYPE === FirestoreChangeTypes.FIRESTORE_CHANGE_TYPE_REMOVED
            ) {
              return pickBy(results, (result) => result[identityAttr] !== item[identityAttr]);
            }

            return {
              ...results,
              ...upsert<T>(stateItems, { ...item, ...modifier }, identityAttr),
            };
          },
          stateItems,
        );
      }
    } else {
      if (
        !preserveRemovedObjects
        && upsertItem
        && upsertItem.FIRESTORE_CHANGE_TYPE
        && upsertItem.FIRESTORE_CHANGE_TYPE === FirestoreChangeTypes.FIRESTORE_CHANGE_TYPE_REMOVED
      ) {
        return pickBy(stateItems, (result) => result[identityAttr] !== upsertItem[identityAttr]);
      }

      return {
        ...stateItems,
        ...upsert<T>(stateItems, upsertItem as T, identityAttr),
      };
    }
  }

  if (!stateItems) {
    return {};
  }

  return stateItems;
};

export const extractUserProperties = (user: ExtendedParsedToken | ExtendedUser): ResultFirebaseToken => {
  if (!user) {
    return user;
  }

  const {
    aud,
    auth_time: authTime,
    exp,
    firebase: firebaseClient,
    iat,
    iss,
    picture,
    metadata,
    sub,
    user_id: userId,
    ...restProps
  } = user;

  const result = {
    ...restProps,
    id: user.impersonateId || user.id || sub,
  };

  return result;
};

export const extractSharedActions = <T>(adapter: EntityAdapter<T>) => ({
  addOne: adapter.addOne,
  addMany: adapter.addMany,
  removeOne: adapter.removeOne,
  removeMany: adapter.removeMany,
  updateOne: adapter.updateOne,
  updateMany: adapter.updateMany,
  upsertOne: adapter.upsertOne,
  upsertMany: adapter.upsertMany,
  setStatus: (state: any, action: PayloadAction<boolean>) => ({
    ...state,
    status: action.payload,
  }),
});

export const firestoreDocumentExists = (documentReference: FirebaseFirestore.DocumentSnapshot): boolean => {
  if (isFunction(documentReference.exists)) {
    return documentReference.exists();
  }

  // node SDK returns a boolean here instead of a function
  return documentReference.exists as any;
};
